// Story 8.2 (ADO-2011), Slice 1 — the cart's total case count (AC5 "how many cases
// the cart holds in total"). Quantities are in cases (Epic 8 context), so the total
// is the sum of line quantities. Shared by the header trigger, the side panel pill
// and footer, and the /cart page so the three never disagree. The cart's
// money value (`cartValue`) is summed from the same lines.
import type { BasketLine } from '../api/basket.client';
import type { PriceKind } from '../api/price-quotes.client';
import type { MoneyValue } from '../shared/money';
import { realPricesEnabled } from '../pricing/realPrices';

/** Pricing migration 31 (ADO-1958), AC5: a line's Mix & Match free units count towards the
 *  cases (towards the order quantity), never towards `cartValue` or `cartDeposit`. */
export function totalCases(lines: readonly BasketLine[]): number {
  return lines.reduce((sum, line) => sum + line.quantity + (line.freeUnitsEarned ?? 0), 0);
}

/** Shown for a total on the /cart page while the review is loading or failed: an unknown
 *  total, in the title, the depot header and the Order footer alike, never a false 0. */
export const UNKNOWN_TOTAL = '–';

/** "1 case" / "N cases" — the pill and footer copy. */
export function casesLabel(count: number): string {
  return count === 1 ? '1 case' : `${count} cases`;
}

/** "1 line" / "N lines": an open cart's line count, on the choice screen and the switcher. */
export function linesLabel(count: number): string {
  return count === 1 ? '1 line' : `${count} lines`;
}

/** The currency of an empty cart's value. */
const EMPTY_CART_CURRENCY = 'EUR';

/** A Money amount in integer cents, so a sum of many lines carries no float drift. */
export function toCents(amount: number): number {
  return Math.round(amount * 100);
}

/**
 * The cart's money value, the sum of its lines' locked prices.
 * Each line counts its stored `lineTotal`, else `unitPrice × quantity`, summed in integer
 * cents. `null` (unknown, never a false figure) when any line has neither price, when an
 * amount is not finite, or when the lines' currencies differ. An empty cart is €0.00.
 */
export function cartValue(
  lines: readonly {
    readonly quantity: number;
    readonly unitPrice?: MoneyValue;
    readonly lineTotal?: MoneyValue;
  }[],
): MoneyValue | null {
  let cents = 0;
  let currency: string | null = null;
  for (const line of lines) {
    let lineCents: number;
    let lineCurrency: string;
    if (line.lineTotal !== undefined) {
      lineCents = toCents(line.lineTotal.amount);
      lineCurrency = line.lineTotal.currency;
    } else if (line.unitPrice !== undefined) {
      lineCents = toCents(line.unitPrice.amount) * line.quantity;
      lineCurrency = line.unitPrice.currency;
    } else {
      return null;
    }
    if (!Number.isFinite(lineCents)) return null;
    if (currency !== null && lineCurrency !== currency) return null;
    currency = lineCurrency;
    cents += lineCents;
  }
  return { amount: cents / 100, currency: currency ?? EMPTY_CART_CURRENCY };
}

/**
 * The cart's deposit total (ADO-2246), the sum of its lines' locked deposits, in integer cents
 * as the order's deposit total is. Each line counts its `lineDeposit`, else `depositPerUnit ×
 * quantity` (an optimistic quantity change keeps the locked rate but drops the stale line
 * deposit, as it drops the stale line total). `null` (unknown, never a false `0.00`) when any
 * line carries neither, when an amount is not finite, or when the lines' currencies differ.
 * An empty cart is €0.00. The deposit is never part of `cartValue`.
 */
export function cartDeposit(
  lines: readonly {
    readonly quantity: number;
    readonly depositPerUnit?: MoneyValue;
    readonly lineDeposit?: MoneyValue;
  }[],
): MoneyValue | null {
  let cents = 0;
  let currency: string | null = null;
  for (const line of lines) {
    let lineCents: number;
    let lineCurrency: string;
    if (line.lineDeposit !== undefined) {
      lineCents = toCents(line.lineDeposit.amount);
      lineCurrency = line.lineDeposit.currency;
    } else if (line.depositPerUnit !== undefined) {
      lineCents = toCents(line.depositPerUnit.amount) * line.quantity;
      lineCurrency = line.depositPerUnit.currency;
    } else {
      return null;
    }
    if (!Number.isFinite(lineCents)) return null;
    if (currency !== null && lineCurrency !== currency) return null;
    currency = lineCurrency;
    cents += lineCents;
  }
  return { amount: cents / 100, currency: currency ?? EMPTY_CART_CURRENCY };
}

/**
 * Whether a deposit figure that is hidden at a known zero is drawn: when it is positive, or
 * unknown (so the dash can say so, never a false zero). A known zero draws nothing. Two callers
 * follow it: the side panel's "Total deposit" row (ADO-2246, D-3) and the /cart page's
 * department band "Deposit €X" (ADO-2250, D-4). The /cart page's own Total deposit rows are
 * always drawn, €0.00 included (ADO-2250, D-1), so they do not use it. The one place the rule
 * lives, so it is a one-line change if design disagrees.
 */
export function showsDepositTotal(deposit: MoneyValue | null): boolean {
  return deposit === null || deposit.amount > 0;
}

/** The figures a cart line's saving is worked out from (Pricing migration 18, ADO-2249, DEC-6):
 *  both the side panel's lines and the /cart review's lines carry them. */
interface SavingLine {
  readonly quantity: number;
  readonly unitPrice?: MoneyValue;
  readonly wasPrice?: MoneyValue;
  readonly priceKind?: PriceKind;
}

/**
 * The price kind the cart explains a line by (ADO-2249, DEC-18): the line's `priceKind` while
 * VITE_REAL_PRICES_ENABLED is on, and absent while it is off, the same rule as the product
 * surfaces (Part A). Row 12 prices every line with the stand-in while the API's pricing data is
 * off, and sends those lines as `standard` (its D8), so without this rule every cart would show
 * a zero saving worked out from stand-in prices. Every savings figure and the line's price-type
 * pill read the kind through here, so the flag gates them in one place; it goes with the flag at
 * row 17's flag exit.
 */
export function explainedPriceKind(line: {
  readonly priceKind?: PriceKind;
}): PriceKind | undefined {
  return realPricesEnabled() ? line.priceKind : undefined;
}

/** A line saving that cannot be worked out from the stored figures. */
export const UNKNOWN_SAVING = 'unknown';

/**
 * A line's saving (ADO-2249, AC5): `(wasPrice − unitPrice) × quantity` in whole cents. `null`
 * when the line is not a promotion or has no was price (only a promotion has a saving, DEC-6), or
 * the was price is not above the price; {@link UNKNOWN_SAVING} when it has a was price but no unit
 * price, an amount that is not finite, or two currencies.
 */
export function lineSaving(line: SavingLine): MoneyValue | null | typeof UNKNOWN_SAVING {
  const { wasPrice } = line;
  if (explainedPriceKind(line) !== 'promotion' || wasPrice === undefined) return null;
  return savingOnCases(wasPrice, line.unitPrice, line.quantity);
}

/**
 * `(wasPrice − unitPrice) × cases` in whole cents: the cart's saving arithmetic, which the card's
 * and the product page's promotion text share (ADO-2249, BWG feedback 5 Nov), so the three agree.
 * `null` when the was price is not above the price; {@link UNKNOWN_SAVING} with no unit price, an
 * amount that is not finite, or two currencies.
 */
export function savingOnCases(
  wasPrice: MoneyValue,
  unitPrice: MoneyValue | undefined,
  cases: number,
): MoneyValue | null | typeof UNKNOWN_SAVING {
  if (unitPrice === undefined || unitPrice.currency !== wasPrice.currency) return UNKNOWN_SAVING;
  const cents = (toCents(wasPrice.amount) - toCents(unitPrice.amount)) * cases;
  if (!Number.isFinite(cents)) return UNKNOWN_SAVING;
  return cents > 0 ? { amount: cents / 100, currency: wasPrice.currency } : null;
}

/**
 * Whether the cart's lines carry the savings fields at all (ADO-2249, DEC-6): a priced line
 * always carries `priceKind` since row 12, so a cart where no line has one is "not sent", and
 * shows no price-type pill, no `Total saving` and no `Savings` card. False whenever
 * VITE_REAL_PRICES_ENABLED is off ({@link explainedPriceKind}, DEC-18).
 */
export function cartSendsSavings(lines: readonly SavingLine[]): boolean {
  return lines.some((line) => explainedPriceKind(line) !== undefined);
}

/** The lines' savings, or `null` as soon as one is unknown (a line without `priceKind` beside
 *  lines that carry one, such as an optimistic add, counts as unknown). */
function lineSavings(lines: readonly SavingLine[]): (MoneyValue | null)[] | null {
  const savings: (MoneyValue | null)[] = [];
  for (const line of lines) {
    if (explainedPriceKind(line) === undefined) return null;
    const saving = lineSaving(line);
    if (saving === UNKNOWN_SAVING) return null;
    savings.push(saving);
  }
  return savings;
}

/**
 * The cart's total saving (ADO-2249, AC6), `cartDeposit`'s shape: the sum of the lines that
 * have a saving in integer cents, `€0.00` when none does; `null` (unknown, the dash
 * `UNKNOWN_TOTAL`, never a partial sum) when any line's saving is unknown or the savings'
 * currencies differ. Read only when {@link cartSendsSavings} is true.
 */
export function cartSaving(lines: readonly SavingLine[]): MoneyValue | null {
  const savings = lineSavings(lines);
  return savings === null ? null : totalOf(savings, lines);
}

/** The sum of the lines' savings, or `null` when their currencies differ. */
function totalOf(
  savings: readonly (MoneyValue | null)[],
  lines: readonly SavingLine[],
): MoneyValue | null {
  let cents = 0;
  let currency: string | null = null;
  for (const saving of savings) {
    if (saving === null) continue;
    if (currency !== null && saving.currency !== currency) return null;
    currency = saving.currency;
    cents += toCents(saving.amount);
  }
  const fallbackCurrency = lines.find((line) => line.unitPrice !== undefined)?.unitPrice?.currency;
  return { amount: cents / 100, currency: currency ?? fallbackCurrency ?? EMPTY_CART_CURRENCY };
}

/** How many lines have a saving: the `N lines` of the review's `Promotions · N lines` (ADO-2249,
 *  AC6). `null` whenever {@link cartSaving} is. */
export function promotionLines(lines: readonly SavingLine[]): number | null {
  const savings = lineSavings(lines);
  if (savings === null || totalOf(savings, lines) === null) return null;
  return savings.filter((saving) => saving !== null).length;
}
